import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import getQueryKeys from "common/datahooks/getQueryKeys";
import { post } from "common/helpers/HTTP";
import { getProxyConfigPayload } from "common/helpers/utils";

async function setProxyRequest(
  workspaceId: string,
  accountId: string,
  proxy: string,
  abortSignal?: AbortSignal,
) {
  await post(
    `workspaces/${workspaceId}/accounts/${accountId}/proxy`,
    {
      proxy_config: getProxyConfigPayload(proxy),
    },
    abortSignal,
  );
}

export default function useAccountProxyMutations() {
  const queryClient = useQueryClient();
  const { id: workspaceId } = useSelectedWorkspaceContext();

  const { proxy: accountProxyQueryKey } =
    getQueryKeys(workspaceId).accountsKeys;

  const { mutateAsync: setAccountProxy, isPending: isSettingAccountProxy } =
    useMutation({
      mutationFn: ({
        accountId,
        proxy,
        abortSignal,
      }: {
        accountId: string;
        proxy: string;
        abortSignal?: AbortSignal;
      }) => setProxyRequest(workspaceId, accountId, proxy, abortSignal),
      onMutate: ({ accountId, proxy: newProxy }) => {
        const previousProxy = queryClient.getQueryData<string>(
          accountProxyQueryKey(accountId),
        );

        queryClient.setQueryData(accountProxyQueryKey(accountId), newProxy);

        return previousProxy;
      },
      onError: (_, { accountId }, previousProxy) => {
        queryClient.setQueryData(
          accountProxyQueryKey(accountId),
          previousProxy,
        );

        toast.error("Error updating proxy");
      },
    });

  return {
    setAccountProxy,
    isSettingAccountProxy,
  };
}
